/** @format */

import {
  AnalyticsService,
  FeatureConfigurationService,
  UtilityService,
  WINDOW,
} from '@shared/data-access/services';
import {
  Chain,
  CompareBarProduct,
  CriteoPinnedContentSpot,
  CriteoTrackingItem,
  CriteoVideo,
  CustomBadge,
  CustomBadgeFeatureParams,
  Environment,
  PlpInGridSponsoredProduct,
  ProductVO,
} from '@shared/data-access/interfaces';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  afterNextRender,
  afterRenderEffect,
  computed,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CriteoVideoComponent, GoogleAdManagerComponent } from '@shared/components';
import {
  DisplayableProduct,
  DisplayableProductListData,
  DisplayableProductListItem,
  PersonalizedPCS,
  PinnedContentSpot,
  PzPlpBannerItem,
} from '@plp/data-access/interfaces';
import { ProductCardComponent, SkeletonProductCardComponent } from '@shared/totf/components';
import {
  ProductListCompareService,
  ProductListLayoutService,
  ProductListMlService,
  ProductListSnackbarService,
  ProductListTrackingService,
} from '@plp/data-access/services';
import { concatMap, of, take } from 'rxjs';
import {
  featureConfigCustomBadgesSet,
  selectCriteoProducts,
  selectCustomBadges,
  selectDisplayableProductListData,
  selectFeatureFlags,
  selectIsAntiClearance,
  selectPageSize,
  selectPinnedContent,
  selectProductList,
  selectProductListAvailability,
  selectProductListFromTransferState,
  selectProductListPromotion,
  selectProductListSEO,
  selectPzPlpBanners,
  selectSelectedFacetsWithCount,
  selectSelectedSort,
  selectSkeletonLoader,
} from '@plp/data-access/state';
import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { selectPageType, selectUserStoreInfo } from '@state/data-access';

import { AuthenticationService } from '@shared/authentication/data-access';
import { COMPARE_PRODUCT_LIST_LIMIT } from '@compare/data-access';
// Had to import this way so that '@shared/favorites/components' doesn't eagerly load the ProductRecommendationsComponent;
import { FavoritesIconComponent } from '@shared/favorites/components';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { FeatureConfigurationKeys } from '@shared/data-access/enums';
import { IncludesPipe } from '@plp/utils';
import { MLStore } from '@machine-learning/data-access';
import { MathFloorPipe } from '@shared/utils/pipes';
import { NoFilteredResultsComponent } from '../no-filtered-results/no-filtered-results.component';
import { PersonalizedBannerComponent } from '../personalized-banner/personalized-banner.component';
import { PinnedContentComponent } from '../pinned-content/pinned-content.component';
import { ResolvePromoPipe } from '../pipes/resolve-promo.pipe';
import { Store } from '@ngrx/store';
import { TransformPricesPipe } from '../pipes';
import { WishListService } from '@shared/favorites/data-access/services';
import { selectCompareBarProducts } from '@plp/data-access/compare';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'plp-components-product-list-grid',
  imports: [
    TransformPricesPipe,
    ResolvePromoPipe,
    IncludesPipe,
    MathFloorPipe,
    FavoritesIconComponent,
    ProductCardComponent,
    PinnedContentComponent,
    NoFilteredResultsComponent,
    SkeletonProductCardComponent,
    GoogleAdManagerComponent,
    CriteoVideoComponent,
    PersonalizedBannerComponent,
  ],
  templateUrl: './product-list-grid.component.html',
  styleUrl: './product-list-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListGridComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  readonly chainAbbr = signal<string>('');
  private readonly chain = inject(Chain);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureConfigurationService = inject(FeatureConfigurationService);
  private readonly plpMlService = inject(ProductListMlService);
  private readonly plpCompareService = inject(ProductListCompareService);
  private readonly plpSnackbarService = inject(ProductListSnackbarService);
  private readonly authService = inject(AuthenticationService);
  private readonly wishlistService = inject(WishListService);
  private readonly elementRef = inject(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly environment = inject(Environment);
  private readonly utilityService = inject(UtilityService);
  private readonly window = inject<Window>(WINDOW);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly productListLayoutService = inject(ProductListLayoutService);
  private readonly productListTrackingService = inject(ProductListTrackingService);

  /** ** START OF FEATURE FLAGS PROPERTIES ************************/
  readonly featureFlags = this.store.selectSignal(selectFeatureFlags);

  readonly availabilityList$ = this.store.selectSignal(selectProductListAvailability);
  readonly availabilityCollection = computed(() => {
    const data = this.availabilityList$();

    return !data || Array.isArray(data) ? null : data;
  });

  readonly customBadges$ = this.store.selectSignal(selectCustomBadges);
  readonly compareBarProducts$ = this.store.selectSignal(selectCompareBarProducts);
  readonly productListSEOData = this.store.selectSignal(selectProductListSEO);
  readonly productListData = this.store.selectSignal(selectProductList);
  readonly promoList$ = this.store.selectSignal(selectProductListPromotion);
  readonly selectedFacetsWithCount = this.store.selectSignal(selectSelectedFacetsWithCount);
  readonly showSkeletonLoader = this.store.selectSignal(selectSkeletonLoader);
  readonly pageSizeStore = this.store.selectSignal(selectPageSize);
  readonly selectedSort = this.store.selectSignal(selectSelectedSort);
  readonly displayableProductListData = this.store.selectSignal(selectDisplayableProductListData);
  readonly selectedStoreInfo = this.store.selectSignal(selectUserStoreInfo);
  readonly isAntiClearance = this.store.selectSignal(selectIsAntiClearance);

  readonly criteoProducts = this.store.selectSignal(selectCriteoProducts);
  readonly displayableCriteoProducts = computed(() => {
    const products = this.criteoProducts();
    const hasInGrid = (products?.placements?.inGrid?.[0]?.products?.length ?? 0) > 0;
    const hasDigitalShelfTalker = !!products?.placements?.digitalShelfTalker;
    return hasInGrid || hasDigitalShelfTalker ? products : null;
  });

  readonly pinnedContentProducts = this.store.selectSignal(selectPinnedContent);
  readonly isProductListFromTransferState = this.store.selectSignal(
    selectProductListFromTransferState,
  );

  /** Raw PZ PLP banner list from NgRx state (null = not yet loaded, [] = empty/errored). */
  readonly pzPersonalizedBanners = this.store.selectSignal(selectPzPlpBanners);

  /**
   * Deduplicated banner list for rendering. Filters out duplicate rowPositions so that
   * only the first banner at each grid row is shown (defensive guard against CF data issues).
   */
  readonly pzVisibleBanners = computed((): PzPlpBannerItem[] => {
    const banners = this.pzPersonalizedBanners();
    if (!banners?.length) return [];
    const seenRows = new Set<number>();
    return banners.filter((banner) => {
      if (seenRows.has(banner.rowPosition)) return false;
      seenRows.add(banner.rowPosition);
      return true;
    });
  });

  readonly isAuthenticated = signal<boolean>(false);
  readonly showAvailabilityData = computed(() => {
    const availabilityData = this.availabilityList$();

    if (Array.isArray(availabilityData) && availabilityData.length === 0) {
      return false;
    }

    return true;
  });

  private readonly validDisplayableProductListData = computed(() => {
    const dpld = this.displayableProductListData();

    /**
     * Skip if criteo products not loaded, pinned content not loaded, or product list is still the
     * one from transfer state (indicating the real product list data hasn't loaded yet)
     */
    if (
      isPlatformServer(this.platformId) ||
      this.criteoProducts() === null ||
      this.pinnedContentProducts() === null ||
      this.isProductListFromTransferState()
    ) {
      return null;
    }

    // Filter: check if dpld has length
    if (!dpld?.length) {
      return null;
    }

    // SkipWhile: skip if skeleton loader is showing, no dpld length, or no product list data
    if (this.showSkeletonLoader() || !dpld.length || !this.productListData()) {
      return null;
    }

    return dpld;
  });

  readonly MAX_NUMBER_ITEMS_PER_ROW = 4;
  readonly DEFAULT_IMAGE_PAGE_POSITION = 0;
  readonly IMAGE_PAGE_POSITION_5 = 5;

  protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

  readonly loaderDummyArray = computed(() =>
    this.pageSizeStore() > 0 ? new Array(this.pageSizeStore()) : [],
  );

  readonly numberOfItemsPerRow = this.productListLayoutService.itemsPerRow;
  isCompareAnalyticsTriggered = false;

  readonly compareCheckboxEnabled = computed(() => this.featureFlags().compareCheckboxEnabled);
  readonly plpShowCustomerRating = computed(() => this.featureFlags().plpShowCustomerRating);
  readonly enableTooltipSurvey = computed(() => this.featureFlags().enableTooltipSurvey);
  readonly isPLPMLIntegrationEnabled = computed(
    () => this.featureFlags().isPLPMLIntegrationEnabled,
  );

  readonly golfCartCheckoutIconEnabled = computed(
    () => this.featureFlags().golfCartCheckoutIconEnabled,
  );

  readonly moreOptionsQVEnabled = computed(() => this.featureFlags().moreOptionsQVEnabled);

  readonly moreOptionsPDPEnabled = computed(() => this.featureFlags().moreOptionsPDPEnabled);

  readonly isUsingCriteoSPAAdServer = computed(() => this.featureFlags().isUsingCriteoSPAAdServer);

  readonly sortingByValidValues = computed(() =>
    this.isSortNumberValid(this.selectedSort()?.value ?? 0),
  );

  readonly compareListEcodes: Signal<string[]> = computed(() =>
    this.compareBarProducts$().map((cp) => cp.ecode),
  );

  readonly productCardReferrer = this.store.selectSignal(selectPageType);

  /**
   * @description
   * Criteo tracking metadata transformed for the data-em attr.
   */
  readonly criteoDataEms = signal<string[]>([]);

  criteoTracking: CriteoTrackingItem[] = [];
  readonly criteoVideoPlacement: Signal<CriteoVideo | undefined> = computed(
    () => this.criteoProducts()?.placements?.videos?.[0],
  );

  readonly hasCriteoVideo: Signal<boolean> = computed(
    () =>
      this.featureFlags().isCriteoVideoEnabled &&
      (this.criteoVideoPlacement()?.products?.length ?? 0) > 0,
  );

  /**
   * Determines the desktop grid row for the Google Ad Manager slot.
   * Iterates banner rowPositions in ascending order and advances the candidate ad row
   * each time a banner occupies or precedes it, handling consecutive banners correctly.
   * Without personalization, pzVisibleBanners() is empty and the result equals the
   * original fixed value (4, or 5 with Criteo) — no behavioral change.
   */
  readonly gridBannerRow: Signal<number> = computed(() =>
    this.computeAdRow(this.hasCriteoVideo() ? 5 : 4),
  );

  /**
   * Determines the mobile grid row for the Google Ad Manager slot.
   * Mirrors gridBannerRow but for mobile (base row 6).
   * Without personalization, pzVisibleBanners() is empty and the result equals 6 —
   * matching the CSS default var(--grid-row-mobile, 6). No behavioral change.
   */
  readonly gridBannerRowMobile: Signal<number> = computed(() => this.computeAdRow(6));

  /**
   * Computes the ad row index starting from baseRow, incrementing it
   * each time a banner occupies or precedes it.
   */
  private computeAdRow(baseRow: number): number {
    const sortedPositions = this.pzVisibleBanners()
      .map((banner) => banner.rowPosition)
      .sort((a, b) => a - b);
    let adRow = baseRow;
    for (const pos of sortedPositions) {
      if (pos >= adRow) break;
      adRow++;
    }
    return adRow;
  }

  constructor() {
    this.chainAbbr.set(this.chain.chainIdentifierAbbr);

    afterRenderEffect(() => {
      const dpld = this.validDisplayableProductListData();

      if (!dpld) {
        return;
      }

      if (this.isPLPMLIntegrationEnabled()) {
        // Don't reset observers when anything other than the data (or whether we should observe in the first place) are changed
        untracked(() => {
          this.utilityService.runWhenIdle(() => {
            this.productListTrackingService.setupProductCardObservers(
              this.elementRef,
              dpld,
              this.productListData(),
              this.selectedFacetsWithCount(),
              this.chainAbbr(),
              this.isUsingCriteoSPAAdServer(),
            );
          });
        });
      }

      if (this.isUsingCriteoSPAAdServer()) {
        const criteoProducts = this.displayableCriteoProducts();

        if (criteoProducts) {
          this.utilityService.runWhenIdle(() => {
            const criteoTracking = this.buildCriteoTracking(dpld);
            this.productListTrackingService.addSponsoredIntersectionObservers(
              criteoProducts,
              criteoTracking,
            );
          });
        }
      }
    });

    afterNextRender(() => {
      this.productListLayoutService.setItemsPerRow();
    });
  }

  ngOnInit(): void {
    this.featureConfigurationService.onFeature(
      FeatureConfigurationKeys.SET_PRODUCT_CUSTOM_BADGE,
      (params) => {
        if (this.isCustomBadgeFeatureParams(params)) {
          this.setCustomBadges(params);
        }
      },
    );

    this.productListLayoutService.windowResizeHandler();

    if (
      isPlatformBrowser(this.platformId) &&
      this.environment.thirdParty.auth0[this.chain.chainIdentifierAbbr].enabled
    ) {
      this.getIsAuthenticated();
    }
  }

  private buildCriteoTracking(
    dpld: DisplayableProductListData,
  ): (CriteoTrackingItem | undefined)[] {
    const eventType = this.productCardReferrer() === 'srlp' ? 'viewSearchResult' : 'viewCategory';
    const experience = globalThis._dsgTag?.experience ?? 'Desktop';
    const tracking: (CriteoTrackingItem | undefined)[] = [];
    const dataEms: string[] = [];

    dpld.forEach((item, index) => {
      let trackingItem: CriteoTrackingItem | undefined;

      if ('lineItemId' in item) {
        trackingItem = {
          vendor: 'Criteo',
          lineItemId: item.lineItemId ?? '',
          placements: `${eventType}_API_${experience}_InGrid`,
          slot: index + 1,
        };
      } else if ('onViewBeacon' in item) {
        trackingItem = {
          vendor: 'Criteo',
          lineItemId: item.csid,
          placements: `${eventType}_API_${experience}_DigitalShelfTalker`,
          slot: index + 1,
        };
      }

      if (trackingItem) {
        tracking[index] = trackingItem;
        dataEms[index] =
          `Criteo:${trackingItem.lineItemId}:${trackingItem.placements}:${trackingItem.slot}`;
        this.analyticsService.triggerAnalyticsEvent('contentRendered', trackingItem);
      }
    });

    this.criteoDataEms.set(dataEms);

    return tracking;
  }

  recordClickEvent(
    item: { event: Event; pdpUrl: string },
    product: ProductVO | PlpInGridSponsoredProduct,
    index: number,
  ): void {
    if (!this.isPLPMLIntegrationEnabled() || !isPlatformBrowser(this.platformId)) {
      return;
    }
    // This is used here to prevent the product-card's <a> tag from navigating to the PDP before the Click Event is sent.
    // Navigation is handled below in the complete() callback.
    item.event.preventDefault();
    // catgroupId is only utilized on family pages, not search. If -1 is detected, an empty string is ultimately sent in the API payload.
    const catgroupId: number | null = this.productListSEOData()?.catgroupId ?? null;

    this.plpMlService.sendClickEventData(
      MLStore[this.chainAbbr().toUpperCase() as keyof typeof MLStore],
      product,
      this.displayableProductListData(),
      this.productListData(),
      0,
      catgroupId,
      index,
    );

    this.window.location.href = item.pdpUrl;
  }

  handleCompareSelection(event: { compareData: CompareBarProduct; isChecked: boolean }): void {
    if (event.isChecked && this.compareListEcodes().length >= COMPARE_PRODUCT_LIST_LIMIT) {
      this.plpSnackbarService.openSnackbar(
        'Only 5 products can be added to compare. Please remove a product before making another selection.',
      );

      return;
    }

    if (event.isChecked) {
      this.utilityService.deferOnPrerendering(() => this.compareCheckboxUsedAnalytics());
      const compareSource = this.productListSEOData()?.id
        ? `PLP: ${this.productListSEOData()?.id}`
        : `Search: ${this.productListData()?.searchVO?.searchTerm}`;
      event.compareData.compareSource = compareSource;
      event.compareData.gridImageURL = this.plpCompareService.modifyImgSrcWidthAndHeight(
        event.compareData.imgURL,
      );

      this.plpCompareService.addProductToCompareList(event.compareData);
    } else {
      this.plpCompareService.removeProductFromCompareList(event.compareData.ecode);
    }
  }

  // To be removed once the compare feature goes to 100% in produciton.
  private compareCheckboxUsedAnalytics(): void {
    // Trigger analytics on compare checkbox click only once per page view
    if (!this.isCompareAnalyticsTriggered) {
      this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
        event: 'compareCheckboxUsed',
      });
      this.isCompareAnalyticsTriggered = true;
    }
  }

  private isCustomBadgeFeatureParams(params: unknown): params is CustomBadgeFeatureParams {
    if (!params || typeof params !== 'object') {
      return false;
    }

    const { ecodes, text, textColor, background } = params as Record<string, unknown>;

    return (
      Array.isArray(ecodes) &&
      ecodes.every((ecode: unknown) => typeof ecode === 'string') &&
      typeof text === 'string' &&
      typeof textColor === 'string' &&
      typeof background === 'string'
    );
  }

  private setCustomBadges(customBadgeParams: CustomBadgeFeatureParams | undefined): void {
    if (!customBadgeParams) {
      return;
    }

    const customBadges: Record<string, CustomBadge> = {};
    const { ecodes, text, textColor, background } = customBadgeParams;

    if (ecodes && text && textColor && background) {
      ecodes.forEach((ecode) => {
        customBadges[ecode] = {
          text,
          textColor,
          background,
        };
      });

      this.store.dispatch(featureConfigCustomBadgesSet({ customBadges }));
    }
  }

  private getIsAuthenticated(): void {
    this.authService
      .getIsAuthenticated$()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap((authResponse: boolean) => {
          this.isAuthenticated.set(authResponse);
          if (!this.isAuthenticated()) {
            return of([]);
          }
          this.window.addEventListener('updateFavoritesStoreData', () => {
            this.updateFavoritesFromQuickview();
          });

          return this.wishlistService.getFavoritedEcodes();
        }),
      )
      .subscribe();
  }

  private updateFavoritesFromQuickview() {
    this.wishlistService.getFavoritedEcodes().pipe(take(1)).subscribe();
  }

  protected trackDPLD(index: number, item: DisplayableProductListItem): string {
    // Optional combo of properties from each type of card
    // Normal = name + parentPartnumber, criteo = product_id, Pinned Content = headline
    let value = index.toString();
    if ('name' in item) {
      value += `-${item.name}-${item?.parentPartnumber}`;
    }
    if ('product_id' in item) {
      value += `-${item.product_id}`;
    }
    if ('headline' in item) {
      value += `-${item.headline}`;
    }
    return value;
  }

  protected isProductVo(item: DisplayableProductListItem): item is DisplayableProduct {
    return 'parentPartnumber' in item;
  }

  protected isSponsoredProduct(
    item: DisplayableProductListItem,
  ): item is PlpInGridSponsoredProduct {
    return 'sponsored' in item;
  }

  protected isPinnedContent(
    item: DisplayableProductListItem,
  ): item is PinnedContentSpot | CriteoPinnedContentSpot | PersonalizedPCS {
    return 'pinnedPosition' in item;
  }

  protected pinnedOnViewBeacon(
    item: PinnedContentSpot | CriteoPinnedContentSpot | PersonalizedPCS,
  ): string {
    return 'onViewBeacon' in item ? item.onViewBeacon : '';
  }

  isSortNumberValid(selectedSortNum: number): boolean {
    return !(selectedSortNum === 2 || selectedSortNum === 3 || selectedSortNum === 4);
  }

  ngOnDestroy(): void {
    this.productListLayoutService.cleanupListeners();
  }
}
